import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ShieldCheck, Star, ChevronRight } from 'lucide-react';
import { locations } from '../../data/locations';
import { services } from '../../data/services';

export function Footer() {
  const currentYear = new Date().getFullYear();

  // Group locations by region
  const groupedLocations = locations.reduce((acc, loc) => {
    if (!acc[loc.region]) acc[loc.region] = [];
    acc[loc.region].push(loc);
    return acc;
  }, {} as Record<string, typeof locations>);

  return (
    <footer className="bg-brand-black text-white pt-20 border-t-[6px] border-brand-red">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Top Features/Trust Bar inside footer */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 pb-12 border-b border-white/10">
          <div className="flex items-center gap-4">
            <ShieldCheck className="w-10 h-10 text-brand-red" strokeWidth={1.5} />
            <div>
              <h3 className="font-bold text-lg">Zertifizierter Betrieb</h3>
              <p className="text-gray-300 text-sm">Geprüfte Qualität & Fachkräfte</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Phone className="w-10 h-10 text-brand-red" strokeWidth={1.5} />
            <div>
              <h3 className="font-bold text-lg">24/7 Erreichbarkeit</h3>
              <p className="text-gray-300 text-sm">365 Tage im Jahr für Sie da</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Star className="w-10 h-10 text-brand-gold" strokeWidth={1.5} />
            <div>
              <h3 className="font-bold text-lg">Top Kundenzufriedenheit</h3>
              <p className="text-gray-300 text-sm">Hervorragende Bewertungen</p>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16">
          
          {/* Brand Col */}
          <div className="lg:col-span-4">
             <Link to="/" className="flex items-center gap-3 mb-6 group">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-red rounded shadow-lg group-hover:bg-white transition-colors duration-300">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white group-hover:text-brand-red transition-colors duration-300">
                    <path d="M2 19H22V21H2V19ZM3 17L5 7L12 12L19 7L21 17H3Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-2xl text-white tracking-wide uppercase leading-none">
                    König
                  </span>
                  <span className="text-brand-gold text-[10px] font-bold tracking-[0.2em] uppercase mt-1">
                    Rohrreinigung
                  </span>
                </div>
              </Link>
              <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-sm">
                Königliche Qualität. Sofortige Hilfe. Ihr zuverlässiger Partner für Rohrreinigung und Klempner Notdienst in ganz Deutschland. Wir setzen auf Transparenz und höchste handwerkliche Standards.
              </p>
          </div>

          {/* Services Col */}
          <div className="lg:col-span-2">
            <h2 className="font-heading font-bold text-lg mb-6 text-white uppercase tracking-wider">Leistungen</h2>
            <ul className="space-y-4">
              {services.map(service => (
                <li key={service.id}>
                  <Link to={`/leistungen/${service.slug}`} className="text-gray-300 hover:text-brand-red transition-colors text-sm flex items-center gap-2 group">
                    <ChevronRight className="w-3 h-3 text-brand-red/0 group-hover:text-brand-red transition-all -ml-5 group-hover:ml-0" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Col */}
          <div className="lg:col-span-2">
            <h2 className="font-heading font-bold text-lg mb-6 text-white uppercase tracking-wider">Unternehmen</h2>
            <ul className="space-y-4">
              <li><Link to="/ueber-uns" className="text-gray-300 hover:text-brand-red transition-colors text-sm">Über uns</Link></li>
              <li><Link to="/preise" className="text-gray-300 hover:text-brand-red transition-colors text-sm">Preise & Transparenz</Link></li>
              <li><Link to="/karriere" className="text-gray-300 hover:text-brand-red transition-colors text-sm">Karriere</Link></li>
              <li><Link to="/kontakt" className="text-gray-300 hover:text-brand-red transition-colors text-sm">Kontakt</Link></li>
            </ul>
          </div>
          
          {/* Contact Box */}
          <div className="lg:col-span-4">
            <div className="bg-white/5 p-8 rounded-2xl border border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-red/10 rounded-full blur-3xl transform translate-x-10 -translate-y-10"></div>
              
              <h3 className="font-heading font-bold text-sm mb-2 text-brand-gold uppercase tracking-wider">Notdienst Zentrale</h3>
              <p className="text-gray-300 text-sm mb-6">24/7 kostenfrei für Sie erreichbar.</p>
              
              <a href="tel:+4915219576394" className="flex items-center gap-4 text-2xl lg:text-3xl font-heading font-bold text-white hover:text-brand-red transition-colors mb-6 group">
                <div className="w-12 h-12 bg-brand-red rounded-full flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                +49 152 19576394
              </a>
              
              <a href="mailto:info@konig-rohrreinigung-klempner-notdienst.de" className="flex items-center gap-3 text-gray-300 hover:text-brand-red transition-colors text-sm font-medium">
                <Mail className="w-5 h-5 text-brand-gold" />
                info@konig-rohrreinigung-klempner-notdienst.de
              </a>
            </div>
          </div>
        </div>

        {/* Locations Directory Matrix */}
        <div className="border-t border-white/10 pt-12 mb-12">
           <h2 className="font-heading font-bold text-center text-sm mb-8 text-brand-gold uppercase tracking-widest">Regionale Einsatzgebiete</h2>
           <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-x-8">
             {Object.entries(groupedLocations).map(([region, locs]) => (
               <div key={region} className="mb-8">
                 <h3 className="font-bold text-white mb-4 text-xs border-b border-white/10 pb-2 flex items-center gap-2 uppercase break-after-avoid">
                   <MapPin className="w-3 h-3 text-brand-red shrink-0" />
                   {region}
                 </h3>
                 <ul className="flex flex-col gap-2">
                   {locs.map(loc => (
                     <li key={loc.id} className="break-inside-avoid">
                       <Link to={`/standorte/${loc.region.toLowerCase().replace(/[^a-z0-9]+/g, '-')}/${loc.slug}`} className="text-gray-300 hover:text-white transition-colors text-xs flex items-center gap-1.5 group">
                         <span className="w-1 h-1 rounded-full bg-brand-red/50 group-hover:bg-brand-red shrink-0"></span>
                         {loc.name}
                       </Link>
                     </li>
                   ))}
                 </ul>
               </div>
             ))}
           </div>
        </div>

        {/* Bottom Legal */}
        <div className="border-t border-white/10 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-gray-400 text-xs text-center md:text-left">
            &copy; {currentYear} König Rohrreinigung & Klempner Notdienst. Alle Rechte vorbehalten.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/impressum" className="text-gray-400 hover:text-white transition-colors text-xs font-medium uppercase tracking-wider">Impressum</Link>
            <Link to="/datenschutz" className="text-gray-400 hover:text-white transition-colors text-xs font-medium uppercase tracking-wider">Datenschutz</Link>
            <Link to="/agb" className="text-gray-400 hover:text-white transition-colors text-xs font-medium uppercase tracking-wider">AGB</Link>
          </div>
        </div>

      </div>
    </footer>
  );
}
